// src/pages/ProfilePage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { profileService } from '../services/profileService';
import { readNotificationFlag } from '../services/desktopNotificationService';
import { useAuth } from '../contexts/AuthContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import type {
  ProfileData,
  NotificationSettings,
  ItemSetting,
  ItemSettingDetail,
} from '../types/profileTypes';
import { ImageUploadModal } from '../components/Profile/ImageUploadModal';
import './ProfilePage.css';

// ──────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────

function getInitials(name: string): string
{
  return name
    .split(' ')
    .slice(0, 2)
    .map(n => n[0])
    .join('')
    .toUpperCase();
}

// ──────────────────────────────────────────────
// Change-Password Modal
// ──────────────────────────────────────────────

interface ChangePasswordModalProps {
  onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ onClose }) =>
{
  const { changePassword } = useAuth();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) =>
  {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword)
    {
      setError('New password and confirmation do not match.');
      return;
    }
    if (newPassword.length < 6)
    {
      setError('New password must be at least 6 characters.');
      return;
    }

    setSaving(true);
    try
    {
      await changePassword({ oldPassword, newPassword, confirmPassword });
      setSuccess('Password changed successfully.');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(onClose, 1500);
    }
    catch
    {
      setError('Failed to change password. Check your current password and try again.');
    }
    finally
    {
      setSaving(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) =>
  {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="profile-modal-backdrop" onClick={handleBackdropClick}>
      <div className="profile-modal">
        <p className="profile-modal-title">Change Password</p>
        <form onSubmit={handleSubmit} autoComplete="off">
          <div className="profile-modal-field">
            <label>Current Password</label>
            <input
              type="password"
              value={oldPassword}
              onChange={e => setOldPassword(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="profile-modal-field">
            <label>New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              required
            />
          </div>
          <div className="profile-modal-field">
            <label>Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
            />
          </div>
          {error && <p className="profile-modal-error">{error}</p>}
          {success && <p className="profile-modal-success">{success}</p>}
          <div className="profile-modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────
// Main Page
// ──────────────────────────────────────────────

export const ProfilePage: React.FC = () =>
{
  const { isDarkMode, setDarkMode: setDarkModeGlobal } = useDarkMode();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [notifications, setNotifications] = useState<NotificationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  // Only show Change Password if user is NOT using external login (SSO)
  const canChangePassword = (() =>
  {
    try
    {
      const raw = sessionStorage.getItem('C2_data');
      if (!raw) return true; // default: allow
      const data = JSON.parse(raw);
      return data?.ExternalLogin !== true;
    }
    catch
    {
      return true;
    }
  })();

  // Local checkbox states mirroring profile fields
  const [notifPerDay, setNotifPerDay] = useState(false);
  const [notifPerInterval, setNotifPerInterval] = useState(false);
  const [dailyDueDate, setDailyDueDateEmail] = useState(false);
  // Desktop notifications have two levels: the browser permission (only the user can revoke it)
  // and this app-level flag, which is what the chat checks before raising a notification.
  const supportsDesktopNotif = typeof window !== 'undefined' && 'Notification' in window;
  const [notifPermission, setNotifPermission] = useState<NotificationPermission | 'unsupported'>(
    () => (supportsDesktopNotif ? Notification.permission : 'unsupported'),
  );
  const [desktopNotif, setDesktopNotif] = useState(
    () =>
      supportsDesktopNotif &&
      Notification.permission !== 'denied' &&
      // With no stored preference, follow whatever the browser already granted
      readNotificationFlag('C2_messageNotification', Notification.permission === 'granted'),
  );
  // Sound is independent from the desktop notification setting, same as the chat
  const [soundNotif, setSoundNotif] = useState(() =>
    readNotificationFlag('C2_soundNotification', true),
  );
  const [enableCache, setEnableCache] = useState(true);
  const [darkMode, setDarkMode] = useState(isDarkMode);
  const [enableHelpTooltips, setEnableHelpTooltips] = useState(
    () => localStorage.getItem('C2_enableHelpTooltips') !== 'false',
  );
  const [enableQuickTutorial, setEnableQuickTutorial] = useState(
    () => localStorage.getItem('C2_enableQuickTutorial') !== 'false',
  );

  // Item settings (notification by item type)
  const [itemSettings, setItemSettings] = useState<ItemSetting[]>([]);
  // Item settings details (event types)
  const [itemDetails, setItemDetails] = useState<ItemSettingDetail[]>([]);

  // ── Fetch ──────────────────────────────────
  useEffect(() =>
  {
    const fetchAll = async () =>
    {
      setLoading(true);
      try
      {
        const [profileData, notifData] = await Promise.all([
          profileService.getProfile(),
          profileService.getNotificationSettings(),
        ]);

        setProfile(profileData);
        setNotifications(notifData);

        // Sync checkbox states
        setNotifPerDay(profileData.NotificationsPerDay);
        setNotifPerInterval(profileData.NotificationsPerInterval);
        setDailyDueDateEmail(profileData.DailyDueDateEmail);
        // localStorage is the switch the chat reads; fall back to the server
        // profile the first time this device has no stored preference
        if (localStorage.getItem('C2_soundNotification') === null)
        {
          setSoundNotif(profileData.soundNotification);
        }

        // Enable cache from localStorage or default true
        const cachedEnable = localStorage.getItem('C2_enableCache');
        setEnableCache(cachedEnable !== null ? cachedEnable === 'true' : true);

        // Dark mode from context (already applied globally)
        setDarkMode(isDarkMode);

        // Item settings from notifications
        setItemSettings(notifData.ItemSettings || []);
        setItemDetails(notifData.ItemSettingsDetails || []);
      }
      catch (err)
      {
        console.error('Error loading profile:', err);
        showToast('Failed to load profile data.', 'error');
      }
      finally
      {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // The user can change the browser permission from the address bar without reloading
  useEffect(() =>
  {
    if (!supportsDesktopNotif || !navigator.permissions?.query) return;

    let status: PermissionStatus | null = null;
    const onPermissionChange = () =>
    {
      const next = Notification.permission;
      setNotifPermission(next);
      // Once the browser blocks them the app-level flag can no longer be honoured
      if (next === 'denied') setDesktopNotif(false);
    };

    navigator.permissions
      .query({ name: 'notifications' as PermissionName })
      .then(res =>
      {
        status = res;
        res.addEventListener('change', onPermissionChange);
      })
      .catch(() =>
      {
        // Not supported (Safari): the permission is only re-read on reload
      });

    return () => status?.removeEventListener('change', onPermissionChange);
  }, [supportsDesktopNotif]);

  // ── Toast helper ───────────────────────────
  const showToast = useCallback((msg: string, type: 'success' | 'error') =>
  {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 5000);
  }, []);

  // ── Image upload ───────────────────────────
  const handleImageClick = () =>
  {
    setShowImageModal(true);
  };

  const handleImageSave = async (blob: Blob, filename: string) =>
  {
    const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
    try
    {
      const result = await profileService.uploadProfileImage(file);
      if (result?.ImageName)
      {
        setProfile(prev => (prev ? { ...prev, ImageName: result.ImageName } : prev));
      }
      else
      {
        const objectUrl = URL.createObjectURL(blob);
        setProfile(prev => (prev ? { ...prev, ImageName: objectUrl } : prev));
      }
    }
    catch
    {
      showToast('Failed to upload image.', 'error');
    }
  };

  // ── Desktop notifications toggle ─────────
  const handleDesktopNotifChange = async (checked: boolean) =>
  {
    // Turning it off only clears the app-level flag: a page cannot revoke the browser permission
    if (!checked)
    {
      setDesktopNotif(false);
      return;
    }

    if (!supportsDesktopNotif) return;

    if (Notification.permission === 'denied')
    {
      setDesktopNotif(false);
      showToast(
        'Notifications are blocked in your browser. Allow them in the browser settings and try again.',
        'error',
      );
      return;
    }

    if (Notification.permission === 'default')
    {
      const permission = await Notification.requestPermission();
      setNotifPermission(permission);
      if (permission !== 'granted')
      {
        setDesktopNotif(false);
        showToast('Notifications were blocked. Please allow them in your browser settings.', 'error');
        return;
      }
    }

    setDesktopNotif(true);
  };

  // Browser permission indicator shown next to the desktop notifications checkbox
  const permissionBadge = (() =>
  {
    switch (notifPermission)
    {
      case 'unsupported':
        return {
          text: 'No support',
          modifier: 'blocked',
          title: 'This browser does not support desktop notifications.',
        };
      case 'granted':
        return {
          text: 'Allowed',
          modifier: 'allowed',
          title: 'The browser allows desktop notifications for Corgee.',
        };
      case 'denied':
        return {
          text: 'Blocked',
          modifier: 'blocked',
          title: 'The browser blocks desktop notifications. Allow them in the browser settings.',
        };
      default:
        return {
          text: 'Not requested',
          modifier: 'pending',
          title: 'The browser has not been asked yet. Tick the checkbox to request it.',
        };
    }
  })();

  // ── Toggle item setting ────────────────────
  const toggleItemSetting = (id: number) =>
  {
    setItemSettings(prev =>
      prev.map(s => (s.ItemSettingId === id ? { ...s, IsSelected: !s.IsSelected } : s)),
    );
  };

  const toggleItemDetail = (id: number) =>
  {
    setItemDetails(prev =>
      prev.map(d => (d.ItemSettingsDetailId === id ? { ...d, IsSelected: !d.IsSelected } : d)),
    );
  };

  // ── Save ───────────────────────────────────
  const handleSave = async () =>
  {
    if (!profile || !notifications) return;
    setSaving(true);
    try
    {
      const payload = {
        ...profile,
        NotificationsPerDay: notifPerDay,
        NotificationsPerInterval: notifPerInterval,
        DailyDueDateEmail: dailyDueDate,
        soundNotification: soundNotif,
        SettingsNotifications: {
          ItemSettings: itemSettings,
          ItemSettingsDetails: itemDetails,
        },
        givenName: profile.PersonName,
        enableCache,
        numDashboards: 7,
        imgFormatIncorrect: false,
        imgEmpty: !profile.ImageName,
        imglong: false,
        enableDarkMode: darkMode,
      };

      await profileService.saveProfile(payload);

      // Persist cache/dark mode/tooltips flags locally
      localStorage.setItem('C2_enableCache', String(enableCache));
      localStorage.setItem('C2_messageNotification', String(desktopNotif));
      localStorage.setItem('C2_soundNotification', String(soundNotif));
      setDarkModeGlobal(darkMode);
      localStorage.setItem('C2_enableHelpTooltips', String(enableHelpTooltips));
      localStorage.setItem('C2_enableQuickTutorial', String(enableQuickTutorial));

      showToast('Changes saved successfully', 'success');
    }
    catch
    {
      showToast('Failed to save profile.', 'error');
    }
    finally
    {
      setSaving(false);
    }
  };

  // ──────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────

  if (loading)
  {
    return (
      <div className="profile-page">
        <div className="profile-loading">
          <div className="profile-spinner" />
          Loading profile…
        </div>
      </div>
    );
  }

  const avatarUrl = profile?.ImageName || '';

  return (
    <div className="profile-page">
      <h1 className="profile-page-title">Profile</h1>

      {/* ── Account Settings ─────────────────────── */}
      <div className="profile-card">
        <p className="profile-card-title">Account Settings</p>

        <div className="profile-account-row">
          {/* Top row: avatar + name */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '10px' }}>
            <div
              className="profile-avatar-wrapper"
              onClick={handleImageClick}
              title="Change profile picture"
            >
              {avatarUrl ? (
                <img className="profile-avatar" src={avatarUrl} alt="Profile" />
              ) : (
                <div className="profile-avatar-placeholder">
                  {getInitials(profile?.PersonName || 'U')}
                </div>
              )}
              <div className="profile-avatar-overlay">
                <i className="fa fa-camera" />
              </div>
            </div>
            <span className="profile-person-name">{profile?.PersonName}</span>
          </div>

          {/* Button below — only for non-external-login users */}
          {canChangePassword && (
            <button
              className="profile-change-password-btn"
              onClick={() => setShowPasswordModal(true)}
            >
              Change my password
            </button>
          )}
        </div>
      </div>

      {/* ── Help Tooltips ────────────────────────── */}
      <div className="profile-card">
        <div className="profile-inner-section">
          <p className="profile-card-title">Help Tooltips</p>
          <div className="profile-checkbox-row">
            <span className="corgee_com_checkbox">
              <input
                id="profile-cb-help-tooltips"
                type="checkbox"
                checked={enableHelpTooltips}
                onChange={e => setEnableHelpTooltips(e.target.checked)}
              />
              <label htmlFor="profile-cb-help-tooltips"></label>
            </span>
            <label htmlFor="profile-cb-help-tooltips" className="profile-checkbox-text">
              Enable Help Tooltips
            </label>
          </div>
          <div className="profile-checkbox-row">
            <span className="corgee_com_checkbox">
              <input
                id="profile-cb-quick-tutorial"
                type="checkbox"
                checked={enableQuickTutorial}
                onChange={e => setEnableQuickTutorial(e.target.checked)}
              />
              <label htmlFor="profile-cb-quick-tutorial"></label>
            </span>
            <label htmlFor="profile-cb-quick-tutorial" className="profile-checkbox-text">
              Enable Quick Tutorial
            </label>
          </div>
        </div>
      </div>

      {/* ── Settings ─────────────────────────────── */}
      <div className="profile-card">
        <p className="profile-card-title">Settings</p>

        <div className="profile-inner-section">
          {/* Two-column grid: item types | notification frequency */}
          <div className="profile-settings-grid">
            {/* Left: Notifications by Item Type */}
            <div>
              <p className="profile-settings-col-title">Notifications by Item Type</p>
              {itemSettings.map(item => (
                <div key={item.ItemSettingId} className="profile-checkbox-row">
                  <span className="corgee_com_checkbox">
                    <input
                      id={`profile-cb-item-${item.ItemSettingId}`}
                      type="checkbox"
                      checked={item.IsSelected}
                      onChange={() => toggleItemSetting(item.ItemSettingId)}
                    />
                    <label htmlFor={`profile-cb-item-${item.ItemSettingId}`}></label>
                  </span>
                  <label
                    htmlFor={`profile-cb-item-${item.ItemSettingId}`}
                    className="profile-checkbox-text"
                  >
                    <span className={`profile-checkbox-icon ${item.Icon}`} />
                    {item.Text}
                  </label>
                </div>
              ))}

              {/* Event Types sub-section */}
              {itemDetails.length > 0 && (
                <>
                  <p className="profile-event-types-label" style={{ marginTop: 14 }}>
                    Event Types:
                  </p>
                  {itemDetails.map(detail => (
                    <div key={detail.ItemSettingsDetailId} className="profile-checkbox-row">
                      <span className="corgee_com_checkbox">
                        <input
                          id={`profile-cb-detail-${detail.ItemSettingsDetailId}`}
                          type="checkbox"
                          checked={detail.IsSelected}
                          onChange={() => toggleItemDetail(detail.ItemSettingsDetailId)}
                        />
                        <label htmlFor={`profile-cb-detail-${detail.ItemSettingsDetailId}`}></label>
                      </span>
                      <label
                        htmlFor={`profile-cb-detail-${detail.ItemSettingsDetailId}`}
                        className="profile-checkbox-text"
                      >
                        {detail.Name}
                      </label>
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* Right: Notification Frequency */}
            <div>
              <p className="profile-settings-col-title">Notification frequency</p>

              <div className="profile-checkbox-row">
                <span className="corgee_com_checkbox">
                  <input
                    id="profile-cb-notif-per-day"
                    type="checkbox"
                    checked={notifPerDay}
                    onChange={e => setNotifPerDay(e.target.checked)}
                  />
                  <label htmlFor="profile-cb-notif-per-day"></label>
                </span>
                <label htmlFor="profile-cb-notif-per-day" className="profile-checkbox-text">
                  Notifications Per Day
                </label>
              </div>

              <div className="profile-checkbox-row">
                <span className="corgee_com_checkbox">
                  <input
                    id="profile-cb-notif-per-interval"
                    type="checkbox"
                    checked={notifPerInterval}
                    onChange={e => setNotifPerInterval(e.target.checked)}
                  />
                  <label htmlFor="profile-cb-notif-per-interval"></label>
                </span>
                <label htmlFor="profile-cb-notif-per-interval" className="profile-checkbox-text">
                  Notifications Per Interval
                </label>
              </div>

              <div className="profile-checkbox-row">
                <span className="corgee_com_checkbox">
                  <input
                    id="profile-cb-daily-due-date"
                    type="checkbox"
                    checked={dailyDueDate}
                    onChange={e => setDailyDueDateEmail(e.target.checked)}
                  />
                  <label htmlFor="profile-cb-daily-due-date"></label>
                </span>
                <label htmlFor="profile-cb-daily-due-date" className="profile-checkbox-text">
                  Send "Your Corgee Today" email
                </label>
              </div>

              <div className="profile-checkbox-row">
                <span className="corgee_com_checkbox">
                  <input
                    id="profile-cb-desktop-notif"
                    type="checkbox"
                    checked={desktopNotif}
                    disabled={!supportsDesktopNotif}
                    onChange={e => handleDesktopNotifChange(e.target.checked)}
                  />
                  <label htmlFor="profile-cb-desktop-notif"></label>
                </span>
                <label htmlFor="profile-cb-desktop-notif" className="profile-checkbox-text">
                  Activate Desktop Notifications
                  <span
                    className={`profile-permission-badge profile-permission-badge--${permissionBadge.modifier}`}
                    title={permissionBadge.title}
                  >
                    {permissionBadge.text}
                  </span>
                </label>
              </div>

              <div className="profile-checkbox-row">
                <span className="corgee_com_checkbox">
                  <input
                    id="profile-cb-sound-notif"
                    type="checkbox"
                    checked={soundNotif}
                    onChange={e => setSoundNotif(e.target.checked)}
                  />
                  <label htmlFor="profile-cb-sound-notif"></label>
                </span>
                <label htmlFor="profile-cb-sound-notif" className="profile-checkbox-text">
                  Sound on Notifications
                </label>
              </div>

              <hr className="profile-settings-divider" />

              {/* Enable Cache / Dark Mode */}
              <div className="profile-checkbox-row">
                <span className="corgee_com_checkbox">
                  <input
                    id="profile-cb-enable-cache"
                    type="checkbox"
                    checked={enableCache}
                    onChange={e => setEnableCache(e.target.checked)}
                  />
                  <label htmlFor="profile-cb-enable-cache"></label>
                </span>
                <label htmlFor="profile-cb-enable-cache" className="profile-checkbox-text">
                  Enable Cache
                </label>
              </div>

              <div className="profile-checkbox-row">
                <span className="corgee_com_checkbox">
                  <input
                    id="profile-cb-dark-mode"
                    type="checkbox"
                    checked={darkMode}
                    onChange={e =>
                    {
                      setDarkMode(e.target.checked);
                      setDarkModeGlobal(e.target.checked);
                    }}
                  />
                  <label htmlFor="profile-cb-dark-mode"></label>
                </span>
                <label htmlFor="profile-cb-dark-mode" className="profile-checkbox-text">
                  Dark Mode
                  <span className="profile-alpha-badge">Alpha Version</span>
                </label>
              </div>
            </div>
          </div>

          {/* Save */}
          <div className="profile-save-row">
            <button className="profile-save-btn" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Change Password Modal ───────────── */}
      {showPasswordModal && <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />}

      {/* ── Image Upload Modal ──────────────── */}
      {showImageModal && (
        <ImageUploadModal onClose={() => setShowImageModal(false)} onSave={handleImageSave} />
      )}

      {/* ── Toast ────────────────────────────────── */}
      {toast && <div className={`profile-toast ${toast.type}`}>{toast.msg}</div>}
    </div>
  );
};

export default ProfilePage;
